import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Header, Label, Segment, Table } from "semantic-ui-react";
import { formatDate, formatDuration } from "../formatters";

const TABLE_COLUMN_COUNT = 7;

function workflowNames(workflow) {
  const stages = {};
  const steps = {};
  (workflow?.stages || []).forEach((stage) => {
    stages[stage.code] = stage.displayName;
    stage.steps.forEach((step) => {
      steps[`${stage.code}:${step.code}`] = step.displayName;
    });
  });
  return { stages, steps };
}

function displayActivity(event, names) {
  if (!event.stageCode) return "Run";
  const stageName = names.stages[event.stageCode] || event.stageCode;
  if (!event.stepCode) return stageName;
  const stepName = names.steps[`${event.stageCode}:${event.stepCode}`] || event.stepCode;
  return `${stageName} / ${stepName}`;
}

function incidentsForEvent(event, incidents) {
  return incidents.filter((incident) => {
    if (!incident.stageCode) return event.type.startsWith("run.");
    if (incident.stageCode !== event.stageCode) return false;
    return !incident.stepCode || incident.stepCode === event.stepCode;
  });
}

const RunTimeline = ({ events, incidents, workflow }) => {
  const names = useMemo(() => workflowNames(workflow), [workflow]);

  return (
    <Segment className="automation-monitoring__timeline">
      <Header as="h2">Event timeline</Header>
      {!workflow ? (
        <p className="automation-monitoring__notice">Workflow display names are not available for this run.</p>
      ) : null}
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Event time</Table.HeaderCell>
            <Table.HeaderCell>Workflow stage or step</Table.HeaderCell>
            <Table.HeaderCell>Event</Table.HeaderCell>
            <Table.HeaderCell>Duration</Table.HeaderCell>
            <Table.HeaderCell>Attempt</Table.HeaderCell>
            <Table.HeaderCell>Message</Table.HeaderCell>
            <Table.HeaderCell>Incident</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {events.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">No events were recorded for this run.</Table.Cell>
            </Table.Row>
          ) : events.map((event) => {
            const eventIncidents = incidentsForEvent(event, incidents);
            return (
              <Table.Row key={event.eventId}>
                <Table.Cell>{formatDate(event.occurredAt)}</Table.Cell>
                <Table.Cell>{displayActivity(event, names)}</Table.Cell>
                <Table.Cell><code>{event.type}</code></Table.Cell>
                <Table.Cell>{formatDuration(event.durationMs)}</Table.Cell>
                <Table.Cell>{event.attempt || "-"}</Table.Cell>
                <Table.Cell>{event.message || "-"}</Table.Cell>
                <Table.Cell>
                  {eventIncidents.length ? eventIncidents.map((incident) => (
                    <Label
                      key={`${incident.type}-${incident.firstSeenAt}`}
                      basic={!incident.active}
                      color={incident.active ? "red" : "grey"}
                    >
                      {incident.type.replace(/_/g, " ")}
                    </Label>
                  )) : "-"}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </Segment>
  );
};

RunTimeline.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  incidents: PropTypes.arrayOf(PropTypes.object).isRequired,
  workflow: PropTypes.object,
};

RunTimeline.defaultProps = {
  workflow: null,
};

export default RunTimeline;
